/**
 * ActionElementRenderer.jsx
 * =========================
 * Unified component for rendering action trigger elements
 * Handles both preview (non-interactive) and live (interactive) modes
 * 
 * Created: 2025-11-20
 * Purpose: Reusable rendering logic for action UI elements
 */

import React from 'react';

/**
 * Renders an action trigger element (button, link, icon, image, card)
 * @param {Object} actionConfig - Complete action configuration
 * @param {boolean} isLive - If true, event handlers are active; if false, preview only
 * @param {Function} onExecute - Callback function to execute the action
 * @returns {JSX.Element} - Rendered element
 */
const ActionElementRenderer = ({ actionConfig, isLive = false, onExecute = null }) => {
  if (!actionConfig || !actionConfig.triggerElement) {
    return (
      <div style={{ padding: '20px', color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center' }}>
        No trigger element configured
      </div>
    );
  }

  const { triggerElement, triggerEvent, elementConfig = {} } = actionConfig;

  // Build event handlers based on trigger event
  const eventHandlers = {};
  if (isLive && onExecute) {
    switch (triggerEvent) {
      case 'onClick':
        eventHandlers.onClick = (e) => {
          e.preventDefault();
          onExecute(actionConfig);
        };
        break;
      case 'onHover':
        eventHandlers.onMouseEnter = () => onExecute(actionConfig);
        break;
      case 'onChange':
        eventHandlers.onChange = (e) => onExecute(actionConfig);
        break;
      case 'onLoad':
        // onLoad is handled differently (useEffect in parent)
        break;
      default:
        break;
    }
  }

  // Render based on trigger element type
  switch (triggerElement) {
    case 'button':
      return (
        <button
          {...eventHandlers}
          style={{
            backgroundColor: elementConfig.bgColor || '#3B82F6',
            color: elementConfig.textColor || '#FFFFFF',
            borderRadius: elementConfig.borderRadius || '6px',
            padding: elementConfig.padding || '10px 20px',
            fontSize: elementConfig.fontSize || '16px',
            border: 'none',
            cursor: isLive ? 'pointer' : 'default',
            width: elementConfig.width || 'auto',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            opacity: isLive ? 1 : 0.9,
          }}
          disabled={!isLive}
        >
          {elementConfig.label || 'Click Me'}
        </button>
      );

    case 'link':
      return (
        <a
          href={isLive ? '#' : undefined}
          {...eventHandlers}
          style={{
            color: elementConfig.color || '#3B82F6',
            fontSize: elementConfig.fontSize || '16px',
            textDecoration: elementConfig.underline ? 'underline' : 'none',
            cursor: isLive ? 'pointer' : 'default',
            fontWeight: '500',
          }}
        >
          {elementConfig.text || 'Click here'}
        </a>
      );

    case 'icon':
      return (
        <span
          {...eventHandlers}
          style={{
            fontSize: elementConfig.size || '24px',
            color: elementConfig.color || '#3B82F6',
            cursor: isLive ? 'pointer' : 'default',
            display: 'inline-block',
            transition: 'transform 0.2s ease',
          }}
          title={elementConfig.iconName || '→'}
        >
          {elementConfig.iconName || '→'}
        </span>
      );

    case 'image':
      return (
        <img
          src={elementConfig.src || 'https://via.placeholder.com/150'}
          alt={elementConfig.alt || 'Action Image'}
          {...eventHandlers}
          style={{
            width: elementConfig.width || '150px',
            height: elementConfig.height || 'auto',
            borderRadius: elementConfig.borderRadius || '8px',
            cursor: isLive ? 'pointer' : 'default',
            objectFit: 'cover',
            transition: 'transform 0.2s ease',
          }}
        />
      );

    case 'card':
      return (
        <div
          {...eventHandlers}
          style={{
            backgroundColor: elementConfig.bgColor || '#FFFFFF',
            border: `2px solid ${elementConfig.borderColor || '#E5E7EB'}`,
            borderRadius: elementConfig.borderRadius || '8px',
            padding: elementConfig.padding || '20px',
            width: elementConfig.width || '300px',
            cursor: isLive ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            boxShadow: isLive ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <h3 
            style={{ 
              margin: '0 0 10px 0', 
              fontSize: '18px', 
              fontWeight: 'bold',
              color: '#111827'
            }}
          >
            {elementConfig.title || 'Card Title'}
          </h3>
          <p 
            style={{ 
              margin: 0, 
              fontSize: '14px', 
              color: '#6B7280',
              lineHeight: '1.5'
            }}
          >
            {elementConfig.description || 'Card description goes here'}
          </p>
        </div>
      );

    default:
      return (
        <div style={{ padding: '20px', color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center' }}>
          Unknown element type: {triggerElement}
        </div>
      );
  }
};

export default ActionElementRenderer;





